import { styled } from "goober";
import { useCallback, useRef } from "react";
import { playClick } from "../lib/sounds";

const StyledPressButton = styled("button")`
  /* Zero specificity: goober can inject a derived styled(PressButton) rule
     earlier in the sheet, where equal specificity would let this reset override it. */
  :where(&) {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    cursor: pointer;
    /* all: unset drops the UA's manipulation default; without it the click
       fallback below waits out iOS's double-tap-zoom delay */
    touch-action: manipulation;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

interface Props extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "onPointerDown" | "onKeyDown"
> {
  onPress: () => void;
  children: React.ReactNode;
}

export function PressButton({ onPress, children, ...props }: Props) {
  // iOS Safari can cull the pointer stream for taps it initially treats as a
  // scroll/gesture, so pointerdown alone drops presses on touch. Fire on
  // pointerdown for immediacy, and use click as the fallback; handledRef
  // dedupes the click that follows a handled pointerdown.
  const handledRef = useRef(false);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.currentTarget.disabled) return;
      if (event.button !== 0) return;
      handledRef.current = true;
      playClick();
      onPress();
    },
    [onPress]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (handledRef.current) {
        handledRef.current = false;
        return;
      }
      if (event.currentTarget.disabled) return;
      playClick();
      onPress();
    },
    [onPress]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.currentTarget.disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        playClick();
        onPress();
      }
    },
    [onPress]
  );

  return (
    <StyledPressButton
      type="button"
      {...props}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </StyledPressButton>
  );
}
